import { RichTextEditor } from '@juvare/common-form-ui/RichTextEditor';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { getVisibleError, handleWidgetUpdate, isSkipValidate, renderForm, validateCustom } from '@juvare/common-form-ui/WidgetForm/Utils';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import { Alert, Box, FormControl, FormLabel, Typography } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../hooks';
import { useAlertAttachments } from '../../hooks';
import AddAttachmentButton from './Components/AddAttachmentButton';
import AttachmentList from './Components/AttachmentList';
import { useEditorConfig } from './Hooks/useEditorConfig';
import { renderApiErrors } from './Utils/RenderApiErrors';
import { type } from './types';
export const WidgetLongMessage = (props) => {
    var _a, _b, _c, _d;
    const { t } = useAlertUiTranslation();
    const responseOptionsEnabled = ((_a = props.config) === null || _a === void 0 ? void 0 : _a.responseOptionsEnabled) || false;
    const value = props.getData();
    const responsePlaceholder = '${responseUrl}';
    const error = getVisibleError(props);
    const updateFormDataAttachments = (attachments) => {
        var _a;
        props.rootData.emailAttachments = attachments;
        renderForm(props, { changed: true, autoSaveImmediately: (_a = props.config) === null || _a === void 0 ? void 0 : _a.autoSave });
    };
    const { attachments, isLoading, loadingEmailAttachments, error: attachmentError, handleSaveAttachments, handleScanAttachments, handleGetAttachment, handleRemoveAttachment, } = useAlertAttachments(props.rootData.emailAttachments, updateFormDataAttachments);
    const preselectedFilesInfo = React.useMemo(() => ({
        count: attachments.length,
        size: attachments.reduce((sum, file) => { var _a; return sum + ((_a = file.fileSize) !== null && _a !== void 0 ? _a : 0); }, 0),
    }), [attachments]);
    React.useEffect(() => {
        if (attachmentError) {
            showAlertDialog({ title: t('pages.edit.validationFailed'), content: renderApiErrors(attachmentError) });
        }
    }, [t, attachmentError]);
    const { editorInit, handleValidateAndSaveAttachments, inputFileRef } = useEditorConfig({
        handleSaveAttachments,
        preselectedFilesInfo,
        responsePlaceholder,
        additionalRichTextEditorMenuItems: props.rootParams.globalWidgetConfig.additionalRichTextEditorMenuItems,
    });
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: ((_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required) || false, error: !!error, sx: {
            '& .tox-tinymce': {
                // unable to overwrite tinyMCE editor styles by passing props
                border: `1px solid ${!!error ? themeColors.accent.red.bold : 'rgba(0, 0, 0, 0.23)'}`, // rgba(0, 0, 0, 0.23) is MUI default border color
                borderRadius: 1,
            },
        } },
        React.createElement(AddAttachmentButton, { loadingAttachmentUpload: isLoading, onSaveAttachments: (selectedFiles) => handleValidateAndSaveAttachments(selectedFiles, preselectedFilesInfo), inputFileRef: inputFileRef }),
        React.createElement(FormLabel, null, props.config.label),
        React.createElement(RichTextEditor, { data: value || props.config.defaultValue, disabled: (_c = props.config) === null || _c === void 0 ? void 0 : _c.disabled, onEditorChange: (htmlMessage) => {
                var _a;
                props.setData(htmlMessage);
                renderForm(props, { changed: true, autoSaveImmediately: (_a = props.config) === null || _a === void 0 ? void 0 : _a.autoSave });
            }, editorInit: editorInit }),
        React.createElement(FormHelperErrorDescription, { error: error }),
        responseOptionsEnabled && (!props.rootData.htmlMessage || !((_d = props.rootData.htmlMessage) === null || _d === void 0 ? void 0 : _d.includes(responsePlaceholder))) && (React.createElement(Box, { pt: 1 },
            React.createElement(Alert, { severity: 'info' },
                React.createElement(Typography, { variant: 'inherit', sx: { fontWeight: 400 } }, t('pages.edit.richTextEditors.responsePlaceholderText', {
                    placeholder: responsePlaceholder,
                }))))),
        React.createElement(AttachmentList, { showActions: true, emailAttachments: attachments, loadingEmailAttachments: loadingEmailAttachments, onRemoveAttachment: handleRemoveAttachment, onGetAttachment: handleGetAttachment, onScanAttachments: handleScanAttachments, loadingAttachmentsUpload: isLoading })));
};
const WidgetLongMessageEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props),
        React.createElement(WidgetLongMessage, Object.assign({}, props))));
};
export const widgetLongMessage = {
    type,
    createWidget: () => ({
        type,
        key: 'longMessage',
        label: 'Long Message',
    }),
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        const handler = new WidgetHandler(props);
        const prevData = handler.config.responseOptionsEnabled;
        props.config.responseOptionsEnabled = props.rootData.allowResponse;
        return prevData !== props.config.responseOptionsEnabled || handleWidgetUpdate(props);
    },
    component: WidgetLongMessage,
    icon: React.createElement(ForwardToInboxOutlinedIcon, null),
    editorComponent: WidgetLongMessageEditor,
};
